import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getMyVenue, listBookings, dateKey, todayKey,
  BOOKING_LABEL, type VenueProfile, type VenueBooking,
} from '../../lib/venue';
import { ApiError } from '../../lib/apiClient';
import { formatDate, formatTime } from '../../lib/format';
import './VenuePages.css';

/**
 * Venue dashboard.
 *
 * Ordered by what needs an answer: pending booking requests first, then the
 * confirmed dates coming up, then the profile summary.
 *
 * The stats are derived from the bookings list rather than from a stats
 * endpoint, because the engine has none. That is a deliberate limit — every
 * figure here is a count of bookings this venue can already see, so nothing is
 * invented and nothing needs a new query.
 */

function Metric({
  value, label, sub, gold,
}: { value: string | number; label: string; sub?: string; gold?: boolean }) {
  return (
    <div className={`ven-metric${gold ? ' ven-metric--gold' : ''}`}>
      <span className="ven-metric__value">{value}</span>
      <span className="ven-metric__label">{label}</span>
      {sub && <span className="ven-metric__sub">{sub}</span>}
    </div>
  );
}

function BookingRow({ b, action }: { b: VenueBooking; action?: boolean }) {
  return (
    <article className={`ven-card${action ? ' ven-card--action' : ''}`}>
      <div className="ven-row">
        <div className="ven-row__main">
          <span className="ven-row__title">{b.title}</span>
          <span className="ven-row__meta">
            {b.eventDate ? formatDate(b.eventDate) : 'Date TBC'}
            {b.eventTime ? ` · ${formatTime(b.eventTime)}` : ''}
            {b.doorsTime ? ` · doors ${formatTime(b.doorsTime)}` : ''}
          </span>
          <span className="ven-hint">Organizer: {b.organizer || '-'}</span>
        </div>
        <div className="ven-row__side">
          {b.expectedAttendance != null && (
            <span className="ven-hint mono">{b.expectedAttendance} expected</span>
          )}
          <span className={`ven-badge ven-badge--${b.bookingStatus}`}>
            {BOOKING_LABEL[b.bookingStatus]}
          </span>
          <Link className="button button--secondary" to={`/venue/bookings`}>
            {action ? 'Review request' : 'View'}
          </Link>
        </div>
      </div>
    </article>
  );
}

export function VenueDashboard() {
  const [venue, setVenue] = useState<VenueProfile | null>(null);
  const [bookings, setBookings] = useState<VenueBooking[]>([]);
  const [noProfile, setNoProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getMyVenue();
        if (cancelled) return;
        setVenue(v);
        if (!v) {
          setNoProfile(true);
          setLoading(false);
          return;
        }
        const { bookings: rows } = await listBookings();
        if (!cancelled) setBookings(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your venue.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    const today = todayKey();
    const month = today.slice(0, 7);
    const confirmed = bookings.filter((b) => b.bookingStatus === 'confirmed');
    return {
      pending: bookings.filter((b) => b.bookingStatus === 'pending').length,
      confirmedThisMonth: confirmed.filter(
        (b) => b.eventDate && dateKey(b.eventDate).startsWith(month)).length,
      upcoming: confirmed.filter((b) => b.eventDate && dateKey(b.eventDate) >= today),
    };
  }, [bookings]);

  if (loading) return <p className="ven-status">Loading…</p>;

  if (noProfile) {
    return (
      <div className="ven-page ven-page--narrow">
        <header className="ven-header">
          <div>
            <h1 className="ven-title">Set up your venue</h1>
            <p className="ven-lede">
              Your account exists, but there is no venue profile yet, so there is nothing
              for an organizer to book.
            </p>
          </div>
        </header>
        <div className="ven-card">
          <p className="ven-section__note">
            Add the room: capacity, layout, technical specs and what you charge. You can
            edit all of it later, and set your availability calendar once the profile
            exists.
          </p>
          <div className="ven-actions">
            <Link className="button button--primary" to="/venue/profile">Create venue profile</Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) return <p className="ven-status ven-error" role="alert">{error}</p>;
  if (!venue) return null;

  const pending = bookings.filter((b) => b.bookingStatus === 'pending');

  return (
    <div className="ven-page">
      <header className="ven-header">
        <div>
          <h1 className="ven-title">{venue.venueName}</h1>
          <p className="ven-lede">
            {[venue.city, venue.state].filter(Boolean).join(', ') || 'Location not set'}
            {venue.capacity != null ? ` · capacity ${venue.capacity}` : ''}
          </p>
        </div>
        <div className="ven-actions">
          <Link className="button button--secondary" to="/venue/calendar">Calendar</Link>
          <Link className="button button--secondary" to="/venue/profile">Edit profile</Link>
        </div>
      </header>

      <div className="ven-metrics">
        <Metric value={stats.pending} label="Awaiting your response" gold={stats.pending > 0} />
        <Metric value={stats.confirmedThisMonth} label="Confirmed this month" />
        <Metric value={stats.upcoming.length} label="Upcoming confirmed" />
        <Metric
          value={venue.capacity ?? ' | '}
          label="Capacity"
          sub={venue.capacity == null ? 'Not set on your profile' : undefined}
        />
      </div>

      <section className="ven-section">
        <div className="ven-section__head">
          <h2 className="ven-section__title">Booking requests</h2>
          <Link className="ven-breadcrumb" to="/venue/bookings">All bookings →</Link>
        </div>
        {pending.length === 0 ? (
          <p className="ven-hint">Nothing awaiting a response.</p>
        ) : (
          <div className="ven-list">
            {pending.map((b) => <BookingRow key={b.eventId} b={b} action />)}
          </div>
        )}
      </section>

      <section className="ven-section">
        <div className="ven-section__head">
          <h2 className="ven-section__title">Upcoming at this venue</h2>
        </div>
        <p className="ven-section__note">
          Confirmed bookings only. Each one holds its date on your calendar.
        </p>
        {stats.upcoming.length === 0 ? (
          <p className="ven-hint">No confirmed dates coming up.</p>
        ) : (
          <div className="ven-list">
            {stats.upcoming
              .slice()
              .sort((a, b) => dateKey(a.eventDate!).localeCompare(dateKey(b.eventDate!)))
              .map((b) => (
                <article key={b.eventId} className="ven-card">
                  <div className="ven-row">
                    <div className="ven-row__main">
                      <span className="ven-row__title">{b.title}</span>
                      <span className="ven-row__meta">
                        {formatDate(b.eventDate)}
                        {b.eventTime ? ` · ${formatTime(b.eventTime)}` : ''}
                      </span>
                      <span className="ven-hint">Organizer: {b.organizer || '-'}</span>
                    </div>
                    <div className="ven-row__side">
                      {b.expectedAttendance != null && (
                        <span className="ven-hint mono">{b.expectedAttendance} expected</span>
                      )}
                      <Link className="button button--secondary" to={`/venue/events/${b.eventId}`}>
                        Event day
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
          </div>
        )}
      </section>
    </div>
  );
}
